import type { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    pickPreviousHarvest,
    resolveCampaignEnd,
    resolveCampaignStart,
} from '/@/hooks/crop-monitoring/campaign-date-range';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import { useZones } from '/@/hooks/zones';
import { getSessionCountry } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCurrentCampaignYearFromFallowZone, getYearlessDate } from '/@/utils/date';

export interface CampaignDateRange {
    /** Start of the campaign. Null until the fallow zone resolves. */
    start: Dayjs | null;
    /** End of the campaign: the CM harvest date when there is one, otherwise far in the future. */
    end: Dayjs;
    /** Whether `start` is the previous crop monitoring's harvest (not the fallow-min). */
    startsAtHarvest: boolean;
    isLoading: boolean;
}

/**
 * Resolves the [start, end] window of a field's campaign.
 *
 * Start:
 *   - the previous crop monitoring's harvest date, when it falls on/after the PREVIOUS
 *     year's fallow-min date (i.e. it belongs to the immediately preceding cycle);
 *   - otherwise (previous harvest older than that — a full fallow gap — or no previous CM)
 *     the current fallow-min date.
 *
 * End: the current CM's harvest date when it has one, otherwise a date far enough in the
 * future that the whole window shows.
 *
 * The fallow-min date is the fallow zone's `minDate` (MM/DD) anchored to the campaign year,
 * which is derived from the planting date when available, otherwise today.
 */
export const useCampaignDateRange = ({
    field,
    cropMonitoring,
}: {
    field?: number;
    cropMonitoring?: CropMonitoring;
}): CampaignDateRange => {
    const country = useSelector(getSessionCountry);
    const fallowZonesQuery = useZones({ type: 'fallow' }, { enabled: !!country?.code });
    const cmsQuery = useCropMonitorings({ fieldId: field ? String(field) : undefined, page: 1 });

    const fallowMin = useMemo(() => {
        const zones = fallowZonesQuery.data;
        if (!Array.isArray(zones) || !country?.code) return null;

        const zone = zones.find((z) => z.codename?.startsWith(country.code));
        if (!zone?.minDate) return null;

        const referenceDate = cropMonitoring?.plantingDate ? new Date(cropMonitoring.plantingDate) : undefined;
        const year = getCurrentCampaignYearFromFallowZone(zone.minDate, referenceDate);
        if (year == null) return null;

        return getYearlessDate(zone.minDate, year);
    }, [fallowZonesQuery.data, country?.code, cropMonitoring?.plantingDate]);

    const end = useMemo(() => resolveCampaignEnd(cropMonitoring?.harvestDate), [cropMonitoring?.harvestDate]);

    const previousHarvest = useMemo(
        () => pickPreviousHarvest(cmsQuery.data?.results ?? [], cropMonitoring?.id),
        [cmsQuery.data, cropMonitoring?.id]
    );

    const start = useMemo(() => resolveCampaignStart(fallowMin, previousHarvest), [fallowMin, previousHarvest]);

    return {
        start,
        end,
        startsAtHarvest: Boolean(start && previousHarvest && start.isSame(previousHarvest, 'day')),
        isLoading: fallowZonesQuery.isLoading || cmsQuery.isLoading,
    };
};
